/**
 * Reads WHY a `FEATURE_ACCESS_RESTRICTED` refusal happened out of the error.
 *
 * `FeatureAccessGuard` refuses a caller whose feature grants leave a route's
 * feature out, and the envelope's `details` say which of the caller's own grants
 * limit them — `FeatureAccessRestrictedDetailsSchema` is that shape, shared with
 * the backend. This reader resolves those facts and never a sentence; the copy
 * is `feature-restriction-copy.ts`, so the language stays the client's decision.
 *
 * ── EACH FIELD DEGRADES ON ITS OWN ──────────────────────────────────────────
 *
 * The schema is not parsed whole. A backend one deploy behind sends
 * `featureKey` / `featureName` with no `restrictedBy`, and a whole-object parse
 * would fail there and drop the feature name the server DID send. So each field
 * is parsed with its own slice of the schema and falls back to a usable value
 * of the right type — an unnamed feature, no groups, no user grant — which still
 * renders a refusal that says who to ask. A failed parse never blanks the
 * refusal into "nothing happened".
 */

import {
  FEATURE_ACCESS_RESTRICTED_CODE,
  type FeatureAccessRestrictedDetails,
  FeatureAccessRestrictedDetailsSchema
} from "@nexus/types/domain"

import { serverErrorCode, serverErrorDetails } from "@/common/utils/api-errors"

type RestrictingGroup = FeatureAccessRestrictedDetails["restrictedBy"]["groups"][number]

/** What a refused caller can be told about their own restriction. */
export interface FeatureRestriction {
  /** The feature's display name, or `null` when the server did not name it. */
  readonly featureName: string | null
  /** Whether the caller's own direct feature grants leave the feature out. */
  readonly userGrant: boolean
  /** The caller's groups that limit them, each with whether an admin decided. */
  readonly groups: readonly RestrictingGroup[]
}

const { featureName: featureNameSchema, restrictedBy: restrictedBySchema } =
  FeatureAccessRestrictedDetailsSchema.shape

/**
 * The restriction behind a feature-access refusal, or `null` when the error is
 * not one.
 *
 * @param error any thrown value; only the server envelope's code and details are read
 */
export function getFeatureRestriction(error: unknown): FeatureRestriction | null {
  if (serverErrorCode(error) !== FEATURE_ACCESS_RESTRICTED_CODE) return null

  const details = serverErrorDetails(error)
  const name = featureNameSchema.safeParse(details?.featureName)
  const restrictedBy = restrictedBySchema.safeParse(details?.restrictedBy)

  return {
    featureName: name.success && name.data !== "" ? name.data : null,
    userGrant: restrictedBy.success ? restrictedBy.data.userGrant : false,
    groups: restrictedBy.success ? restrictedBy.data.groups : []
  }
}

/** Whether the restriction names anything beyond the feature itself. */
export function explainsRestriction(restriction: FeatureRestriction): boolean {
  return restriction.userGrant || restriction.groups.length > 0
}
